import { AssetResponse, isSportsEvent } from "../FetchAssetDetails/types";

/**
 * Retrieves the provider logo URL from the response object.
 *
 * @param asset - The AssetResponse object containing details about the asset.
 * @returns The formatted provider logo URL or `null` if no default logo is found.
 *
 */
const getProviderLogo = (asset: AssetResponse): string | null => {
  /** Check if channel exists on this asset type */
  if (!("channel" in asset.attributes)) {
    return null;
  }

  const providerLogo = asset.attributes.channel?.logo?.find(
    (l: { type: string; key: string; template: string }) =>
      l.type === "Default",
  );
  if (providerLogo) {
    return providerLogo.template
      .replace("{key}", providerLogo.key)
      .replace("{width}", "0")
      .replace("{height}", "0");
  }
  return null;
};

/**
 * Formats a given `Date` object into a time string.
 *
 * @param date - The `Date` object to format.
 * @returns A formatted time string in the format "HH:MM AM/PM".
 *
 */
const formatTime = (date: Date): string =>
  date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });

/**
 * Formats a given `Date` object into a date string.
 *
 * @param date - The `Date` object to format.
 * @returns A formatted date string in the format "MM/DD".
 *
 */
const formatDate = (date: Date): string =>
  `${date.getMonth() + 1}/${date.getDate()}`;

/**
 * Generates a badge string for a sports event based on its start time.
 *
 * @param asset - The AssetResponse object containing details about the asset.
 * @returns A formatted badge string for the sports event or `null` if the asset is not a sports event.
 *
 * @example
 * ```typescript
 * const asset: AssetResponse = {
 *   attributes: {
 *     displayStartTime: "2023-10-15T13:45:00",
 *   },
 * };
 * const badgeString = fetchBadgeString(response);
 * console.log(badgeString); // Outputs: "1:45 PM" if the event is today or "10/15" otherwise.
 * ```
 */
const fetchBadgeString = (asset: AssetResponse): string | null => {
  if (isSportsEvent(asset)) {
    const badgeDate = new Date(asset.attributes.displayStartTime);
    const today = new Date();
    if (
      today.getDay() == badgeDate.getDay() &&
      today.getMonth() == badgeDate.getMonth() &&
      today.getFullYear() == badgeDate.getFullYear()
    ) {
      return formatTime(badgeDate);
    } else {
      return formatDate(badgeDate);
    }
  } else return null;
};

/**
 * Generates a formatted Title Basics date for a sports event based on its start time.
 * Returns [Month Day, HH:MM AM/PM] for upcoming events
 * Returns [Weekday, Month Day] for replay events
 *
 * @param response - The AssetResponse object containing details about the asset.
 * @returns A formatted date string for the sports event or `null` if the asset is not a sports event.
 *
 */
const formatTitleBasicsDate = (response: AssetResponse): string[] | null => {
  if (isSportsEvent(response)) {
    const date = new Date(response.attributes.displayStartTime);
    const today = new Date();
    const monthDay = new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
    }).format(date);

    /** Upcoming events */
    if (date.getTime() > today.getTime()) {
      const time = formatTime(date);
      return [`${monthDay}`, `${time}`];
    } else {
      /** Replay events */
      const weekday = new Intl.DateTimeFormat("en-US", {
        weekday: "long",
      }).format(date);
      return [`${weekday}, ${monthDay}`];
    }
  } else {
    return null;
  }
};

/**
 * Retrieves genre strings from an asset response with flexible options.
 *
 * @param asset - The AssetResponse object containing genre information.
 * @param options - Configuration options:
 *   - maxGenres: Maximum number of genres to return (default: 2)
 *   - separator: String to use for separating genres (default: " • ")
 *   - indices: Specific genre indices to retrieve (e.g., [0, 2] for 1st and 3rd genre)
 * @returns A formatted string of genres or `null` if no genres are found.
 *
 * @example
 * ```typescript
 * const asset: AssetResponse = {
 *   attributes: {
 *     genres: ["Kids", "Comedy", "Musical", "Animation", "Adventure"],
 *   },
 * };
 *
 * // Get first 2 genres
 * const genreString = fetchGenreString(asset);
 * console.log(genreString); // Outputs: "Kids • Comedy"
 *
 * // Get first genre only
 * const genreString2 = fetchGenreString(asset, { maxGenres: 1 });
 * console.log(genreString2); // Outputs: "Kids"
 *
 * // Get specific genres by index (1st and 3rd)
 * const genreString3 = fetchGenreString(asset, { indices: [0, 2] });
 * console.log(genreString3); // Outputs: "Kids • Musical"
 *
 * // Get 3rd genre only
 * const genreString4 = fetchGenreString(asset, { indices: [2] });
 * console.log(genreString4); // Outputs: "Musical"
 * ```
 */
const fetchGenreString = (
  asset: AssetResponse,
  options?: {
    maxGenres?: number;
    separator?: string;
    indices?: number[];
  },
): string | null => {
  const { maxGenres = 2, separator = " • ", indices } = options || {};

  /** Check if genres exists on this asset type */
  if (!("genres" in asset.attributes)) {
    return null;
  }

  const genres = (asset.attributes as any).genres as string[] | undefined;

  if (!genres || genres.length === 0) {
    return null;
  }

  let selectedGenres: string[];

  if (indices && indices.length > 0) {
    /** Get specific genres by index */
    selectedGenres = indices
      .map((index) => genres[index])
      .filter((genre) => genre !== undefined);
  } else {
    /** Get first N genres */
    selectedGenres = genres.slice(0, maxGenres);
  }

  return selectedGenres.length > 0 ? selectedGenres.join(separator) : null;
};

export {
  getProviderLogo,
  fetchBadgeString,
  formatTitleBasicsDate,
  fetchGenreString,
};
